import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import _get from 'lodash/get'
import {
  Heading,
  Box,
  Text,
  Icon,
  Section,
  Grid,
  Skeleton,
  Stack,
} from '@rebeldotcom/components'

import {
  ErrorBoundary,
  hasDOM,
  get,
  useUserService,
  shapeDomains,
  isInternic,
  useSessionService,
  useOpenExchangeService,
} from '@rebeldotcom/ui'
import { PricesQuery, DisplayPrice } from '../../../utilities/pricing'
import * as productConfigs from '../product-configurators'
import { getAccountDomains } from '../../../utilities/endpoints'
import { DomainOverview } from '../product-configurators/domain-dropdown'
import { Content } from '../../content'
import ConfiguratorSelector from './configurator-selector'
import NewOrExistingCustomerConfig from './new-or-existing-config'
import { SimpleDivider } from '../../my-account/email-manager/ox-email/ox-email-manager'

const propTypes = {
  data: PropTypes.shape({
    allDynamicPrice: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    pagesJson: PropTypes.shape({
      componentName: PropTypes.string,
      configurator: PropTypes.shape({
        addButtonContent: PropTypes.string,
        content: PropTypes.string,
        headerType: PropTypes.string,
        heading: PropTypes.string,
        id: PropTypes.string,
        packages: PropTypes.arrayOf(
          PropTypes.shape({
            addToCartPath: PropTypes.string,
            amount: PropTypes.number,
            costPerUser: PropTypes.shape({}),
            features: PropTypes.arrayOf(PropTypes.string),
            id: PropTypes.string,
            itemCode: PropTypes.string,
            itemDescription: PropTypes.string,
            packageId: PropTypes.number,
            product: PropTypes.string,
            productCategory: PropTypes.string,
            returnPath: PropTypes.string,
            source: PropTypes.string,
            title: PropTypes.string,
          })
        ),
        type: PropTypes.string,
      }).isRequired,
      layoutProps: PropTypes.shape({
        showNewsletterSignup: PropTypes.bool,
      }),
      panels: PropTypes.arrayOf(PropTypes.shape({})),
      reseller: PropTypes.string,
      seo: PropTypes.shape({
        description: PropTypes.string,
        title: PropTypes.string,
      }),
      slug: PropTypes.string,
    }),
  }).isRequired,
}

const ConfiguratorBuilder = ({ data }) => {
  const {
    slug,
    configurator: {
      id,
      headerType,
      heading,
      type,
      packages,
      content,
      addButtonContent,
    },
  } = data.pagesJson
  /* Populate user domains */
  const { user } = useUserService()
  const {
    data: {
      currentCurrency: { code: currentCode },
    },
  } = useSessionService()

  const [newDomain, setNewDomain] = useState(true)
  const [domains, setDomains] = useState([])
  const [domainList, setDomainList] = useState([])
  const [domainsLoading, setDomainsLoading] = useState(true)
  const [selectedPackageObj, setSelectedPackageObj] = useState(null)
  const [currentPackageId, setCurrentPackageId] = useState(0)

  const Configurator = productConfigs[type]
  const CustomHeader = headerType ? productConfigs[headerType] : null
  const multiPackages = packages.length > 1

  const currentPackageIDRef = useRef(null)

  const handlePackageSelection = packageId => {
    const matchingPackage = packages.find(p => p.packageId === packageId)
    setSelectedPackageObj(matchingPackage)
  }
  const allDynamicPrices = PricesQuery()

  // Try / catch for safety. Since this component isn't only used for OX reasons. useOpenExchangeService() doesn't work within the useEffect.
  try {
    // Returns info about the current package.
    const { currentContext } = useOpenExchangeService()
    // Using useRef because a useState would trigger an infinite rerender loop, and global variables are bad practice.
    if (currentContext) currentPackageIDRef.current = currentContext.packageId
  } catch {}

  useEffect(() => {
    let itemCodeParam
    if (hasDOM) {
      const decoded = decodeURIComponent(window.location.search)
      itemCodeParam = _get(queryString.parse(decoded), 'itemCode')
    }

    if (packages && packages.length > 0) {
      if (itemCodeParam) {
        const matchingPackage = packages.find(p => p.itemCode === itemCodeParam)
        if (matchingPackage) {
          setSelectedPackageObj(matchingPackage)
        } else {
          setSelectedPackageObj(packages[0])
        }
        // If currentPackageIDRef.current exists, then this is for the OX package change page. Previously it just defaulted to item 0, as per the next else{} block.
      } else if (currentPackageIDRef.current) {
        setSelectedPackageObj(
          packages.find(_ => _.packageId === currentPackageIDRef.current)
        )
        setCurrentPackageId(currentPackageIDRef.current)
      } else {
        setSelectedPackageObj(packages[0])
      }
    }
  }, [])

  useEffect(() => {
    const getDomains = async () => {
      try {
        setDomainsLoading(true)
        const domainsResponse = await get(getAccountDomains())
        setDomains(shapeDomains(domainsResponse))
        // set it domains loading to false after the next useEffect
      } catch (err) {
        // the Configurator should gracefully fail
        setDomainsLoading(false)
      }
    }

    if (
      user &&
      Configurator &&
      (type === 'DomainProductSearch' || type === 'ExistingDomain')
    ) {
      getDomains()
    }
  }, [user?.customerId])

  useEffect(() => {
    if (domains && domains.length > 0) {
      const filteredDomains = domains.filter(
        domain =>
          !domain.addOns.some(
            addOn =>
              (addOn.key === 'weeblySite' && addOn.status) ||
              (addOn.key === 'emailHosting' && addOn.status) ||
              (addOn.key === 'webHosting' && addOn.status)
          ) && domain.status === 'Active'
      )
      setDomainList(() => {
        setDomainsLoading(false)
        return filteredDomains.map((domain, index) => ({
          component: <DomainOverview key={index} domain={domain} />,
          value: domain.name,
          label: domain.name,
        }))
      })
    }
  }, [domains])

  // Standard configurator returns
  const configuratorReturn = (
    <>
      {Configurator && multiPackages ? (
        // STYLE FOR MULTIPLE PACKAGES
        <Box justifyContent="center" width="100%">
          <Box alignSelf="flex-start" maxWidth="containers.sm">
            <ConfiguratorSelector
              allDynamicPrices={allDynamicPrices}
              onChange={handlePackageSelection}
              packages={packages}
              selectedPackageId={selectedPackageObj?.packageId}
              currentPackageID={currentPackageId}
            />
          </Box>
        </Box>
      ) : (
        // STYLE FOR SINGLE PACKAGES
        <Box
          justifyContent="center"
          minHeight="105px"
          my={[1, 3]}
          px={[4, 0]}
          width="100%"
        >
          <Stack>
            <Box flexDirection="row" justifyContent="space-between">
              <Text mt={3} variant="milliBold">
                WHAT&apos;S INCLUDED
              </Text>
              <Text mt={3} variant="milliBold">
                {selectedPackageObj?.title.toUpperCase()}
                {' - '}
                {DisplayPrice(
                  selectedPackageObj,
                  currentCode,
                  allDynamicPrices
                )}
              </Text>
            </Box>
            <SimpleDivider />
            <Grid
              as="ul"
              display={['flex', 'grid']}
              flexDirection={['column']}
              gridGap={[0, 2]}
              gridTemplateColumns={['1fr 1fr']}
              maxWidth="containers.sm"
              mb={2}
              mt={3}
              width="100%"
            >
              {packages[0].features.map(f => (
                <Box key={f} alignItems="center" as="li">
                  <Icon
                    aria-hidden="true"
                    color={isInternic ? 'blueDark' : 'deepKoamaru'}
                    name="checkmark-fill"
                  />
                  <Text ml={2} variant="pound">
                    {f}
                  </Text>
                </Box>
              ))}
            </Grid>
          </Stack>
        </Box>
      )}

      {Configurator && type === 'DomainProductSearch' && (
        <NewOrExistingCustomerConfig onToggle={x => setNewDomain(x)}>
          {/* Tech Debt: Deprecate the DomainProductSearch configurator, and replace with ExistingDomain/NewDomain */}
          <Configurator
            addButtonContent={addButtonContent}
            content={content}
            domainList={domainList}
            id={id}
            newDomain={newDomain}
            selectedPackage={selectedPackageObj}
            type={type}
            user={user}
          />
        </NewOrExistingCustomerConfig>
      )}
      {Configurator && type === 'ChangeCloudEmailPackage' && (
        <Configurator
          allDynamicPrices={PricesQuery()}
          selectedPackage={selectedPackageObj}
        />
      )}
      {Configurator &&
        (type === 'DomainProduct' || type === 'GoogleWorkspace') && (
          <Box justifyContent="center" width="100%">
            <Box
              alignSelf="flex-start"
              justifyContent="center"
              mb={4}
              px={[2, 0]}
              width="100%"
            >
              <Configurator
                addButtonContent={addButtonContent}
                content={content}
                domainList={domainList}
                id={id}
                newDomain={newDomain}
                selectedPackage={selectedPackageObj}
                type={type}
                user={user}
              />
            </Box>
          </Box>
        )}
      {Configurator && type === 'ExistingDomain' && (
        <NewOrExistingCustomerConfig hideOptionsSelector>
          <Configurator
            domains={domainList}
            domainsLoading={domainsLoading}
            selectedPackage={selectedPackageObj}
            user={user}
          />
        </NewOrExistingCustomerConfig>
      )}
    </>
  )

  return !selectedPackageObj ? (
    <Skeleton height="100vh" />
  ) : (
    <>
      {CustomHeader && <CustomHeader />}
      <Section
        containerProps={{
          maxWidth: 'containers.md',
        }}
        mt={!CustomHeader ? 4 : 0}
        pb={[1, 2]}
        pt={!CustomHeader ? [2, 3] : 1}
        width="100%"
      >
        {/* <Stack alignItems="flex-start" mt={5} width="100%"> */}
        {!CustomHeader && (
          <Box justifyContent="center" mb={1} px={[2, 0]} width="100%">
            <Heading
              as="h1"
              maxWidth="containers.md"
              textAlign="center"
              variant="teraBold"
            >
              {heading}
            </Heading>
          </Box>
        )}
        {content && (
          <Box flexDirection="row" justifyContent="center" px={[2, 0]}>
            <Content textAlign="center" variant="kilo">
              {content}
            </Content>
          </Box>
        )}
      </Section>
      <ErrorBoundary location={`${slug}-${type}`}>
        {configuratorReturn}
      </ErrorBoundary>
    </>
  )
}

ConfiguratorBuilder.propTypes = propTypes

export default ConfiguratorBuilder
